import React, { useCallback, useEffect, useState } from 'react';
import {
    GroupInfo,
    RoleInfo,
    deleteGroup,
    listGroups,
    listRoles,
} from '../../api/authz';
import { ApiError } from '../../api/request';
import { alertDialog, confirmDialog } from '../../utils/dialog';
import GroupEditorModal from './GroupEditorModal';
import GroupMembersModal from './GroupMembersModal';

/**
 * 用户组 (2026-09-13): 把一批用户放进组, 给组配角色, 组内每个成员都获得这些角色.
 * 用户生效权限 = 自己直接绑的角色 ∪ 所在各组的角色 (纯并集, 多组不冲突);
 * 系统角色 (管理员 / 普通用户) 不能挂到组 —— 管理员身份与准入只能对人授予.
 */
function GroupList() {
    const [list, setList] = useState<GroupInfo[]>([]);
    const [roles, setRoles] = useState<RoleInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // editing: null = 关闭; undefined = 新建; GroupInfo = 编辑
    const [editing, setEditing] = useState<GroupInfo | null | undefined>(null);
    const [members, setMembers] = useState<GroupInfo | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listGroups();
            setList(res.list || []);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        listRoles()
            .then((r) => setRoles(r.list || []))
            .catch(() => setRoles([]));
    }, [load]);

    const roleLabel = (name: string) =>
        roles.find((r) => r.name === name)?.displayName || name;

    const onDelete = async (g: GroupInfo) => {
        const hint =
            g.memberCount > 0
                ? `\n组内 ${g.memberCount} 名成员将失去该组带来的角色 (各自直接绑定的角色不受影响)。`
                : '';
        if (
            !(await confirmDialog(
                `确认删除用户组「${g.displayName}」(${g.name})?${hint}`,
            ))
        )
            return;
        try {
            await deleteGroup(g.name);
            await load();
        } catch (e) {
            alertDialog(
                `删除失败: ${e instanceof ApiError ? e.message : String(e)}`,
            );
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4">
                <div className="text-xs leading-relaxed text-slate-500">
                    把一批用户放进组、给组配角色,组内每个成员都获得这些角色;
                    新人入组即生效,调岗移出即收回。
                    <br />
                    用户的生效权限 = 自己直接绑定的角色 +
                    所在各组的角色(并集,多组不冲突);
                    <strong className="text-slate-600">管理员</strong>与
                    <strong className="text-slate-600">普通用户</strong>
                    两个系统角色不能挂到组。
                </div>
                <button
                    type="button"
                    className="btn-primary whitespace-nowrap"
                    onClick={() => setEditing(undefined)}
                >
                    + 新建用户组
                </button>
            </div>

            {error && (
                <div className="card border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                    {error}
                </div>
            )}
            {loading && <div className="text-sm text-slate-400">加载中…</div>}

            <div className="space-y-2">
                {list.map((g) => (
                    <div key={g.name} className="card p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-800">
                                    {g.displayName}
                                </div>
                                <div className="mt-0.5 font-mono text-xs text-slate-400">
                                    {g.name}
                                </div>
                                {g.description && (
                                    <div className="mt-1.5 text-xs text-slate-500">
                                        {g.description}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    title="查看 / 添加 / 移出组成员"
                                    onClick={() => setMembers(g)}
                                >
                                    成员
                                    {g.memberCount > 0
                                        ? ` (${g.memberCount})`
                                        : ''}
                                </button>
                                <button
                                    type="button"
                                    className="btn-ghost"
                                    onClick={() => setEditing(g)}
                                >
                                    编辑
                                </button>
                                <button
                                    type="button"
                                    className="btn-danger-text"
                                    onClick={() => onDelete(g)}
                                >
                                    删除
                                </button>
                            </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-400">
                                组角色
                            </span>
                            {g.roles.length === 0 && (
                                <span className="text-xs text-amber-600">
                                    尚未配置角色 ——
                                    组内成员暂不会因此获得任何权限
                                </span>
                            )}
                            {g.roles.map((r) => (
                                <span
                                    key={r}
                                    className="tag bg-brand-50 text-brand-600"
                                    title={r}
                                >
                                    {roleLabel(r)}
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {!loading && list.length === 0 && (
                <div className="card p-6 text-center text-sm text-slate-400">
                    还没有用户组,点右上角「新建用户组」开始
                </div>
            )}

            <GroupEditorModal
                open={editing !== null}
                source={editing || undefined}
                roles={roles}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    load();
                }}
            />
            <GroupMembersModal
                group={members}
                onClose={() => setMembers(null)}
                onChanged={load}
            />
        </div>
    );
}

export default GroupList;
